// Responsive admin shell: desktop sidebar and mobile top nav.
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  ListChecks,
  Lock,
  LogOut,
  Megaphone,
  RefreshCw,
  Sparkles,
  Trophy,
  Users as UsersIcon,
  UsersRound
} from 'lucide-react';
import type { ReactNode } from 'react';
import { BrandMark } from '../../../components/ui/bits';
import { cn } from '../../../lib/cn';
import { useAdminData } from './context';
import { timeAgo } from './format';

const NAV = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/users', label: 'Users', icon: UsersIcon, end: false },
  { to: '/admin/picks', label: 'Picks', icon: ListChecks, end: false },
  { to: '/admin/leagues', label: 'Leagues', icon: Trophy, end: false },
  { to: '/admin/mini-leagues', label: 'Mini Leagues', icon: UsersRound, end: false },
  { to: '/admin/broadcast', label: 'Broadcast', icon: Megaphone, end: false },
  { to: '/admin/promos', label: 'Promos', icon: Sparkles, end: false }
] as const;

export function AdminShell({ children }: { children: ReactNode }) {
  const { data, isFetching, refetch, lock } = useAdminData();

  return (
    <div className="flex h-full w-full overflow-hidden bg-canvas text-ink">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-black/5 bg-card md:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <BrandMark size={30} />
          <div>
            <div className="text-[15px] font-extrabold leading-tight tracking-tight">HeadToHead</div>
            <div className="text-[11px] font-semibold uppercase tracking-eyebrow text-ink2">Admin</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-3">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-semibold transition-colors',
                  isActive ? 'bg-night text-white' : 'text-ink2 hover:bg-fill'
                )
              }
            >
              <item.icon size={18} strokeWidth={2.2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-black/5 p-3">
          <div className="px-2 pb-2 text-[11px] text-ink3">Updated {timeAgo(data.generatedAt)}</div>
          <button
            type="button"
            onClick={refetch}
            className="tap flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-semibold text-ink2 hover:bg-fill"
          >
            <RefreshCw size={17} className={cn(isFetching && 'animate-spin')} /> Refresh
          </button>
          <button
            type="button"
            onClick={lock}
            className="tap flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-[14px] font-semibold text-ink2 hover:bg-fill"
          >
            <LogOut size={17} /> Lock
          </button>
        </div>
      </aside>

      {/* Main column */}
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile chrome */}
        <div className="md:hidden">
          <header className="flex items-center justify-between gap-2 border-b border-black/5 bg-card px-4 py-3">
            <div className="flex items-center gap-2">
              <BrandMark size={26} />
              <span className="text-[15px] font-extrabold tracking-tight">Admin</span>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={refetch} className="tap grid h-10 w-10 place-items-center rounded-xl text-ink2 hover:bg-fill">
                <RefreshCw size={18} className={cn(isFetching && 'animate-spin')} />
              </button>
              <button type="button" onClick={lock} className="tap grid h-10 w-10 place-items-center rounded-xl text-ink2 hover:bg-fill">
                <Lock size={18} />
              </button>
            </div>
          </header>
          <nav className="no-scrollbar flex gap-1 overflow-x-auto border-b border-black/5 bg-card px-3 py-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors',
                    isActive ? 'bg-night text-white' : 'bg-fill text-ink2'
                  )
                }
              >
                <item.icon size={15} strokeWidth={2.2} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="native-scroll no-scrollbar flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-6xl px-4 py-5 md:px-8 md:py-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
